/**
 * What happened to the mail we sent.
 *
 * The endpoint returns one row per event the mail provider reported, and the
 * screen used to render those rows as a flat table with a single checkbox over
 * it. That answers "what happened to this one message" and nothing else - an
 * operator asking whether mail is getting through at all, or whether a domain
 * started bouncing this morning, had to read a hundred rows and count.
 *
 * So the rows are also read as a population: outcome as a proportion, a bounce
 * and complaint rate against the mail they are a rate *of*, the same split over
 * time, and which recipient domains and failure reasons the trouble is
 * concentrated in.
 *
 * Two populations live on this screen, and the difference between them used to
 * be invisible. The band's three rates are now the API's own split, counted
 * over every event the filters match; the sections below it are still counted
 * in the browser over the events actually loaded, which is the first page
 * until an operator asks for more. A rate is the figure that suffers most from
 * a shifting denominator - "12% bounced" over 50 of 40,000 events and 12% over
 * all 40,000 are different claims that look identical - so the band's rates
 * moved server-side and everything that is still page-local says so.
 *
 * The denominator those rates are taken over is delivery attempts, not events.
 * An open and a click are the same message being read again, not another
 * attempt at sending it; counted in, a popular newsletter would look like it
 * never bounces. The API reports `attempts` explicitly for that reason, so the
 * exclusion is a number on the wire rather than an assumption on each side.
 *
 * What it cannot do: find one named member's mail. The recipient is stored as
 * the first twelve characters of a keyed hash of the address, and nothing in
 * the API maps a member to that hash. An operator handed a reference from a
 * support ticket can search for it; an operator holding an email address
 * cannot turn it into one.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router';
import {
  emailDeliveriesApiV1InternalEmailDeliveriesGet,
  type EmailDeliveryBreakdown,
  type EmailProviderEvent,
} from '@closet-theory/api';
import { Button } from '@ui/components/base/buttons/button';
import { Callout, Code, Section } from '@ui/console/primitives';
import { Term } from '@ui/console/glossary';
import { Quantity, formatCount, formatPercent, plural } from '@ui/console/measures';
import { MetricBand } from '@ui/console/MetricBand';
import type { BandFigure } from '@ui/console/MetricBand';
import { RecordTable } from './record-table';
import { SelectField, TextField } from '@ui/console/forms';
import type { Tone } from '@ui/console/status';
import { configureAdminApi, handleUnauthorized } from './auth';
import { Ranked, Timeline, bucketize, type SeriesKey } from './charts';
import { Actions, DetailList, Filters, Page } from './components';
import { errorDetail } from './operation-policy';

/** The provider's own event names, plus the `unknown` the ingest falls back to. */
export const EVENT_TYPES = [
  'accepted',
  'delivered',
  'opened',
  'clicked',
  'failed',
  'rejected',
  'complained',
  'unsubscribed',
  'stored',
  'unknown',
] as const;

/** The largest page the API will serve. */
export const PAGE_SIZE = 100;

export type Outcome =
  'delivered' | 'accepted' | 'bounced' | 'deferred' | 'complained' | 'engagement' | 'other';

/**
 * The provider reports ten event names at six severities; an operator is
 * asking one of four questions. This is the mapping between the two, kept in
 * one place so the tiles, the bars and the chart cannot disagree.
 */
export function outcomeOf(
  event: Pick<EmailProviderEvent, 'event_type'> & { severity?: string | null },
): Outcome {
  switch (event.event_type) {
    case 'delivered':
      return 'delivered';
    case 'accepted':
      return 'accepted';
    case 'failed':
    case 'rejected':
      // A temporary failure is the provider retrying the same message: the
      // API counts it as deferred, not as a bounce, and so does this.
      return event.severity === 'temporary' ? 'deferred' : 'bounced';
    case 'complained':
    case 'unsubscribed':
      return 'complained';
    case 'opened':
    case 'clicked':
      return 'engagement';
    default:
      return 'other';
  }
}

export const OUTCOMES: readonly (SeriesKey & { id: Outcome; plain: string })[] = [
  {
    id: 'delivered',
    label: 'delivered',
    tone: 'success',
    plain: 'the provider handed it to the recipient’s mail server',
  },
  {
    id: 'accepted',
    label: 'accepted',
    tone: 'info',
    plain: 'we handed it to the provider and it has not reported an outcome yet',
  },
  {
    id: 'bounced',
    label: 'failed or rejected',
    tone: 'error',
    plain: 'it did not reach the recipient',
  },
  {
    id: 'deferred',
    label: 'failed, will retry',
    tone: 'warning',
    plain: 'a temporary failure; the provider is trying the same message again',
  },
  {
    id: 'complained',
    label: 'complained or unsubscribed',
    tone: 'warning',
    plain: 'the recipient asked not to hear from us again',
  },
  {
    id: 'engagement',
    label: 'opened or clicked',
    tone: 'neutral',
    plain: 'the recipient interacted with it',
  },
  { id: 'other', label: 'other', tone: 'neutral', plain: 'stored, or a name we do not recognise' },
];

const OUTCOME_TONE: Record<Outcome, Tone> = {
  delivered: 'success',
  accepted: 'info',
  bounced: 'error',
  deferred: 'warning',
  complained: 'warning',
  engagement: 'neutral',
  other: 'neutral',
};

export type Tally = { total: number; byOutcome: Record<Outcome, number>; suppressed: number };

export function tally(events: readonly EmailProviderEvent[]): Tally {
  const byOutcome: Record<Outcome, number> = {
    delivered: 0,
    accepted: 0,
    bounced: 0,
    deferred: 0,
    complained: 0,
    engagement: 0,
    other: 0,
  };
  let suppressed = 0;
  for (const event of events) {
    byOutcome[outcomeOf(event)] += 1;
    if (event.suppressed) suppressed += 1;
  }
  return { total: events.length, byOutcome, suppressed };
}

/**
 * Delivery attempts, as opposed to every event. An open and a click are the
 * same message being read twice and must not sit in the denominator of a
 * bounce rate, or a popular newsletter would look like it never bounces. A
 * deferred event is the provider retrying the same message, so it is not
 * another attempt either.
 *
 * The band no longer uses this: its denominator is the API's own `attempts`,
 * counted over every matching event rather than the loaded ones. This stays
 * as the browser-side statement of the same exclusion, and its test pins the
 * same event names the API's `attempts` filter does, so the two cannot drift
 * into meaning different things by halves.
 */
export function attempts(counts: Tally['byOutcome']): number {
  return counts.delivered + counts.accepted + counts.bounced + counts.complained;
}

export function countBy<T>(
  items: readonly T[],
  key: (item: T) => string | null,
): Array<{
  id: string;
  count: number;
}> {
  const counts = new Map<string, number>();
  for (const item of items) {
    const value = key(item);
    if (value === null) continue;
    counts.set(value, (counts.get(value) ?? 0) + 1);
  }
  return [...counts]
    .map(([id, count]) => ({ id, count }))
    .sort((left, right) => right.count - left.count);
}

/** What a figure has to say for itself before the read has arrived. */
const NOT_YET = 'The read has not come back yet.';

/** Why there is no rate, as opposed to a rate of nought. */
const NO_ATTEMPTS =
  'No delivery attempt matches these filters, so there is no rate to take. Opens and clicks are not attempts.';

/**
 * One outcome as a share of the delivery attempts these filters match.
 *
 * Both numbers come from the API's own split over the same WHERE the list is
 * built from, so the percentage, its bar and the table underneath all answer
 * for one population. Counted here instead, over the events the browser had
 * pulled, the figure would be a rate over an arbitrary sample presented in the
 * same type as a rate over the filtered set - and a reader has no way to tell
 * which of the two a given percentage is. The denominator is attempts rather
 * than events throughout: an open and a click are the same message being read
 * again, not another attempt, and counting them in understates every failure
 * rate on this screen.
 */
function rateOfAttempts(figure: {
  id: string;
  label: string;
  /** How many of the matching attempts ended this way, from the server. */
  count: number | undefined;
  /** Matching delivery attempts: the denominator for figure and bar alike. */
  attempts: number | undefined;
  /** The bar's sentence, without its numbers: they are appended. */
  reading: string;
  tone: Tone;
  /** Said only when the count is above nought, so a badge names a fact. */
  statusLabel?: string;
  hint: (count: number, attempts: number) => string;
}): BandFigure {
  const { count, attempts: denominator } = figure;
  if (count === undefined || denominator === undefined) {
    return { id: figure.id, label: figure.label, absent: NOT_YET };
  }
  if (denominator === 0) {
    return { id: figure.id, label: figure.label, absent: NO_ATTEMPTS };
  }
  return {
    id: figure.id,
    label: figure.label,
    value: formatPercent(count / denominator),
    raw: `${count} of ${denominator}`,
    ...(count > 0 && figure.statusLabel
      ? { tone: figure.tone, statusLabel: figure.statusLabel }
      : {}),
    bar: {
      fraction: count / denominator,
      label: `${figure.reading}: ${count} of ${denominator} delivery attempts matching these filters`,
      tone: figure.tone,
    },
    hint: figure.hint(count, denominator),
  };
}

export type MailFilters = { eventType: string; suppressed: string; find: string };

export const ALL_MAIL: MailFilters = { eventType: 'all', suppressed: 'all', find: '' };

function OutcomeSplit({ counts, total }: { counts: Tally['byOutcome']; total: number }) {
  return (
    <div className="flex flex-col gap-3 rounded-xl border border-secondary bg-primary p-4">
      <span
        className="flex h-2.5 w-full overflow-hidden rounded-full bg-secondary"
        aria-hidden="true"
      >
        {OUTCOMES.map((outcome) =>
          counts[outcome.id] > 0 ? (
            <span
              key={outcome.id}
              className="block h-full"
              style={{
                width: `${(counts[outcome.id] / Math.max(total, 1)) * 100}%`,
                background: `var(--color-bg-${outcome.tone === 'neutral' ? 'quaternary' : `${outcome.tone}-solid`})`,
              }}
            />
          ) : null,
        )}
      </span>
      <dl className="grid grid-cols-1 gap-2 sm:grid-cols-3">
        {OUTCOMES.map((outcome) => (
          <div key={outcome.id} className="flex min-w-0 flex-col gap-0.5">
            <dt className="text-xs text-tertiary">
              {outcome.label} &middot; {outcome.plain}
            </dt>
            <dd className="m-0 text-sm font-medium text-secondary tabular-nums select-all">
              {formatCount(counts[outcome.id])} &middot;{' '}
              {total > 0 ? formatPercent(counts[outcome.id] / total) : '—'}
            </dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

export function EmailDeliveries() {
  const [params, setParams] = useSearchParams();
  const filters: MailFilters = {
    eventType: params.get('event_type') ?? ALL_MAIL.eventType,
    suppressed: params.get('suppressed') ?? ALL_MAIL.suppressed,
    find: params.get('find') ?? ALL_MAIL.find,
  };

  const [items, setItems] = useState<EmailProviderEvent[]>([]);
  const [total, setTotal] = useState(0);
  // The band's split, counted by the API over everything these filters match.
  // Undefined until a read succeeds, so the band shows why rather than nought.
  const [matching, setMatching] = useState<EmailDeliveryBreakdown>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>();

  const set = (key: keyof MailFilters, value: string) => {
    const next = { ...filters, [key]: value };
    setParams(
      Object.fromEntries(
        Object.entries({
          event_type: next.eventType,
          suppressed: next.suppressed,
          find: next.find,
        }).filter(([, chosen]) => chosen && chosen !== 'all'),
      ),
      { replace: true },
    );
  };

  const load = useCallback(
    async (offset: number) => {
      configureAdminApi();
      setLoading(true);
      const result = await emailDeliveriesApiV1InternalEmailDeliveriesGet({
        query: {
          limit: PAGE_SIZE,
          offset,
          event_type: filters.eventType === 'all' ? null : filters.eventType,
          suppressed: filters.suppressed === 'all' ? null : filters.suppressed === 'yes',
        },
      });
      setLoading(false);
      handleUnauthorized(result.response.status);
      if (result.error || !result.data) {
        setError(errorDetail(result.error, 'request_failed'));
        return;
      }
      setError(undefined);
      setItems((current) => (offset ? [...current, ...result.data.items] : result.data.items));
      setTotal(result.data.total);
      setMatching(result.data.breakdown);
    },
    [filters.eventType, filters.suppressed],
  );

  useEffect(() => {
    void load(0);
  }, [load]);

  const counts = useMemo(() => tally(items), [items]);
  const complete = items.length >= total && total > 0;

  const needle = filters.find.trim().toLowerCase();
  const rows = needle
    ? items.filter((item) =>
        [item.recipient_reference, item.recipient_domain, item.reason_code, item.id]
          .filter((value): value is string => typeof value === 'string')
          .some((value) => value.toLowerCase().includes(needle)),
      )
    : items;

  const buckets = useMemo(
    () => bucketize(items, (item) => new Date(item.occurred_at).getTime(), outcomeOf, 24),
    [items],
  );

  const scope = complete
    ? `all ${formatCount(total)} events the API holds for these filters`
    : `the ${formatCount(items.length)} most recent of ${formatCount(total)} events for these filters`;

  return (
    <Page
      title="Email delivery"
      description="Use this page to see whether our mail is reaching people, how much of it bounces or gets marked as spam, and which recipient domains the trouble is concentrated in."
      band={
        <MetricBand
          label="Whether our mail is arriving, all time"
          figures={[
            rateOfAttempts({
              id: 'delivered',
              label: 'Delivered',
              count: matching?.delivered,
              attempts: matching?.attempts,
              reading: 'Delivered',
              tone: 'success',
              statusLabel: 'reaching people',
              hint: (count, denominator) =>
                `${formatCount(count)} of ${formatCount(denominator)} delivery attempts matching these filters, all time, counted by the API over the whole set rather than the page loaded. Opens, clicks and temporary failures the provider is retrying are not attempts, so they are left out.`,
            }),
            rateOfAttempts({
              id: 'bounced',
              label: 'Bounce rate',
              count: matching?.bounced,
              attempts: matching?.attempts,
              reading: 'Failed or rejected',
              tone: 'error',
              statusLabel: 'not arriving',
              hint: (count, denominator) =>
                `${formatCount(count)} failed or rejected for good out of ${formatCount(denominator)} delivery attempts matching these filters, all time. Temporary failures the provider is retrying are counted separately as deferred, and opens and clicks are left out.`,
            }),
            {
              id: 'deferred',
              label: 'Deferred (will retry)',
              ...(matching
                ? {
                    value: formatCount(matching.deferred ?? 0),
                    unit: plural(matching.deferred ?? 0, 'event'),
                    raw: matching.deferred ?? 0,
                    hint: 'Temporary failures matching these filters, all time. The provider is trying the same message again, so these are not bounces and not extra attempts.',
                  }
                : { absent: NOT_YET }),
            },
            rateOfAttempts({
              id: 'complained',
              label: 'Complaint rate',
              count: matching?.complained,
              attempts: matching?.attempts,
              reading: 'Complained or unsubscribed',
              tone: 'warning',
              statusLabel: 'people opting out',
              hint: (count, denominator) =>
                `${formatCount(count)} marked as spam or unsubscribed out of ${formatCount(denominator)} delivery attempts matching these filters, opens and clicks excluded. A provider withdraws sending reputation over this figure, so it matters well below one percent.`,
            }),
          ]}
        />
      }
      action={
        <Button size="md" color="secondary" isDisabled={loading} onClick={() => void load(0)}>
          {loading ? 'Refreshing…' : 'Refresh'}
        </Button>
      }
    >
      <Callout
        tone="neutral"
        title="The band counts every matching event; everything below it counts the ones loaded"
      >
        The band is the API&rsquo;s own split over all {formatCount(total)} events these filters
        match. Its rates are taken over delivery attempts: opens, clicks and deferred retries are
        not attempts. Everything under this line &mdash; the outcome split, the chart and the three
        breakdowns &mdash; is still counted in this page from the events it has pulled, currently{' '}
        {scope}. Load the rest before quoting one of those.{' '}
        {complete ? 'Everything matching these filters is loaded.' : ''} Addresses are stored as a{' '}
        <Term term="keyed hash" />, so a recipient shows as a twelve-character reference and their
        domain, never their address.
      </Callout>

      {error ? (
        <Callout
          tone="error"
          title="Mail delivery events could not be loaded."
          actions={
            <Button size="sm" color="secondary" onClick={() => void load(0)}>
              Try again
            </Button>
          }
        >
          <Code>{error}</Code>
        </Callout>
      ) : null}

      <Section
        title="Outcome of everything loaded"
        description="Every event, split by what the provider said happened. Opens and clicks are shown but are not delivery attempts."
      >
        <OutcomeSplit counts={counts.byOutcome} total={items.length} />
        <DetailList
          items={[
            [
              <>
                <Term term="suppressed">Suppressed</Term> events
              </>,
              <Quantity
                value={formatCount(counts.suppressed)}
                unit={plural(counts.suppressed, 'event')}
                raw={counts.suppressed}
              />,
            ],
            [
              'Recipient domains seen',
              <Quantity
                value={formatCount(countBy(items, (item) => item.recipient_domain).length)}
                unit={plural(countBy(items, (item) => item.recipient_domain).length, 'domain')}
                raw={countBy(items, (item) => item.recipient_domain).length}
              />,
            ],
          ]}
        />
      </Section>

      <Section
        title="Outcome over time"
        description="The same split across the span the loaded events cover, so a day mail started bouncing is visible as a change of shape rather than as a number that has to be compared with a remembered one. A period with no events is left empty."
      >
        {buckets.length ? (
          <Timeline
            buckets={buckets}
            series={OUTCOMES}
            noun="events"
            caption="Each column is one slice of the span covered by the events loaded. Height is the number of events; colour is the outcome."
          />
        ) : (
          <Callout tone="neutral" title="Nothing to plot yet.">
            No events have been loaded for these filters, so there is no span to draw. This is an
            empty result, not a failed read.
          </Callout>
        )}
      </Section>

      <div className="grid grid-cols-1 items-start gap-5 xl:grid-cols-3">
        <Section title="By event type" description="The provider's own names for what happened.">
          <Ranked
            rows={countBy(items, (item) => item.event_type).map(({ id, count }) => ({
              id,
              label: id,
              count,
              tone: OUTCOME_TONE[outcomeOf({ event_type: id })],
            }))}
            total={items.length}
            unit="events"
            emptyHint="No event has been loaded, so there is nothing to break down."
          />
        </Section>

        <Section
          title="By recipient domain"
          description="Where the mail was going. A single domain dominating the failures is a different problem from failures spread evenly."
        >
          <Ranked
            rows={countBy(
              items.filter((item) => outcomeOf(item) === 'bounced'),
              (item) => item.recipient_domain,
            ).map(({ id, count }) => ({ id, label: id, count, tone: 'error' as const }))}
            total={counts.byOutcome.bounced}
            unit="failures"
            emptyHint="Nothing failed or was rejected among the events loaded, so no domain is implicated."
          />
        </Section>

        <Section
          title="Why it failed"
          description="The reason the provider gave, as a code. A blank reason means it reported none."
        >
          <Ranked
            rows={countBy(items, (item) => item.reason_code).map(({ id, count }) => ({
              id,
              label: id,
              count,
              tone: 'warning' as const,
            }))}
            total={items.length}
            unit="events"
            emptyHint="No event carried a reason code."
          />
        </Section>
      </div>

      <Section
        title="Every event loaded"
        description="One row per event the provider reported. Click a row to see all of its fields."
      >
        <Filters>
          <SelectField
            label="Event type"
            value={filters.eventType}
            onChange={(event) => set('eventType', event.currentTarget.value)}
            hint="Narrows the query itself, so loading more pulls only this type."
            options={[
              { value: 'all', label: 'All' },
              ...EVENT_TYPES.map((value) => ({ value, label: value })),
            ]}
          />
          <SelectField
            label="Suppressed"
            value={filters.suppressed}
            onChange={(event) => set('suppressed', event.currentTarget.value)}
            hint="Addresses the provider refuses to deliver to any more."
            options={[
              { value: 'all', label: 'All' },
              { value: 'yes', label: 'Suppressed only' },
              { value: 'no', label: 'Not suppressed' },
            ]}
          />
          <TextField
            label="Find a recipient reference"
            type="search"
            value={filters.find}
            placeholder="a1b2c3d4e5f6"
            hint="Matches a reference, a domain or a reason among the rows already loaded. An email address cannot be turned into a reference."
            onChange={(event) => set('find', event.currentTarget.value)}
          />
        </Filters>

        <RecordTable
          noun="provider events"
          records={rows.map((item) => ({ ...item, outcome: outcomeOf(item) }))}
          columns={[
            'occurred_at',
            'outcome',
            'event_type',
            'severity',
            'suppressed',
            'recipient_domain',
            'reason_code',
            'recipient_reference',
            'id',
          ]}
          columnLabel={(column) => (column === 'id' ? 'Provider event' : undefined)}
          emptyHint={
            needle
              ? 'Nothing among the loaded events matches that text. The search runs over what is loaded, so loading more may find it.'
              : 'The read succeeded and the provider has reported no event matching these filters. That is an answer, not a failure.'
          }
        />

        <Actions>
          <span className="mr-auto text-sm text-tertiary">
            {formatCount(rows.length)} shown &middot; {formatCount(items.length)} loaded of{' '}
            {formatCount(total)}
          </span>
          <Button
            size="md"
            color="secondary"
            isDisabled={loading || items.length >= total}
            onClick={() => void load(items.length)}
          >
            {items.length >= total ? 'Everything is loaded' : `Load ${PAGE_SIZE} more`}
          </Button>
        </Actions>
      </Section>

      <Callout tone="neutral" title="Finding one member's mail is not possible from here">
        The API stores the recipient as the first twelve characters of a <Term term="keyed hash" />{' '}
        of their address, and nothing in it maps a member to that reference. Given a reference from
        a support ticket you can search for it above; given an email address there is no way to work
        out which reference is theirs. The endpoint also offers no date range, so &ldquo;what
        happened last Tuesday&rdquo; means paging back to it.
      </Callout>
    </Page>
  );
}
